import { useState } from 'react';

import { Box, Divider, Link, Stack, Typography } from '@mui/material';

import { radius } from '@karehero/llama/theme/radius';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import Note from '@/designs-dev/annotations/Note';
import Panel, { SectionTitle } from '@/designs-dev/handover/Panel';
import MiniScreen from '@/designs-dev/handover/MiniScreen';
import ScrollRow from '@/designs-dev/handover/ScrollRow';
import {
  type PlanPreview,
  type PlanSpec,
  type Stage,
  type StageState,
} from '@/designs-dev/plan/types';

// The plan for a project, rendered from one content module. Same frame as a
// flows page — breadcrumb, title, links down the side, then panels — because it
// is the same kind of document and switching between them should not feel like
// changing app.
//
// No minis anywhere on it. Every screen this plan is about is one click away in
// the menu, and a plan that draws them is a flows page with opinions.

/**
 * How a stage draws, per state. Two properties do the work: filled means it
 * exists, and dashed means nobody has committed to when.
 *
 * Lime for behind us, coral for in hand, an outline for drawn and waiting, a
 * dashed outline for unsequenced. No key on the page: the label beside each bar
 * says the same thing in words, and a legend for four states is a second thing
 * to maintain.
 */
const STAGE_STYLE: Record<
  StageState,
  {
    bg: string;
    border: string;
    dashed?: boolean;
    label: string;
  }
> = {
  done: {
    bg: 'custom.limeGreen.500',
    border: 'custom.limeGreen.500',
    label: 'Done',
  },
  nearly: {
    // Hollow, not a paler fill: at swatch size lime 100 and lime 500 were the
    // same colour. Filled means finished, and the colour says which thing.
    //
    // Floral White rather than transparent, because a picked row paints behind
    // it and a transparent bar would fill with the highlight.
    bg: 'background.paper',
    border: 'custom.limeGreen.500',
    label: 'Nearly done',
  },
  building: {
    bg: 'custom.coral.50',
    border: 'custom.coral.600',
    label: 'In progress',
  },
  'not-started': {
    bg: 'background.paper',
    border: 'custom.neutral.400',
    label: 'Not started',
  },
  unordered: {
    bg: 'background.paper',
    border: 'custom.neutral.400',
    dashed: true,
    label: 'Not sequenced',
  },
};

const STATE_ORDER: StageState[] = [
  'done',
  'nearly',
  'building',
  'not-started',
  'unordered',
];

const COLUMNS = 12;

/**
 * The stages, drawn against each other.
 *
 * A twelve-column track per row, and each bar placed in it, so the overlaps are
 * the picture: the calculator opening before onboarding closes, the claim
 * opening before the calculator closes, and the last two sitting on the same
 * columns because nothing has decided which comes first.
 */
const LABEL_COLUMN = '220px';

/**
 * The height of the month row: an overline and the rule under it.
 *
 * Written down rather than measured, because the note sits in the column beside
 * the diagram and has to start where the rule does. If the scale's type changes
 * this is the number that moves.
 */
const AXIS_HEIGHT = 35;

// One row's grid: the name, then the track the bars are placed in.
//
// No column gap. A gap between the twelfths is charged to every bar that spans
// them, so a six-column bar was never twice a three-column one, and at laptop
// width the gaps took more of the row than the columns did — which is what
// squeezed the months into each other. The label keeps its own clearance
// instead, and a twelfth is a twelfth.
const ROW = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    md: `${LABEL_COLUMN} repeat(${COLUMNS}, 1fr)`,
  },
  px: 1,
} as const;

// Clearance after the name, in place of the gap the grid used to carry.
const LABEL_GAP = { pr: 1 };

// The key. Every state, whether or not the diagram uses it yet: it is the
// vocabulary the stages are set in, so a stage becoming Done is one word here
// and nothing else.
const Key = () => (
  <Stack
    sx={{ flexWrap: 'wrap', pb: 1, rowGap: 1, columnGap: 3 }}
    direction='row'
  >
    {STATE_ORDER.map((state) => {
      const style = STAGE_STYLE[state];
      return (
        <Stack
          key={state}
          sx={{ alignItems: 'center' }}
          direction='row'
          spacing={1}
        >
          <Box
            sx={{
              width: 24,
              height: 12,
              bgcolor: style.bg,
              border: 1,
              borderStyle: style.dashed ? 'dashed' : 'solid',
              borderColor: style.border,
              borderRadius: `${radius.sm}px`,
            }}
          />
          <Typography variant='caption' color='text.secondary'>
            {style.label}
          </Typography>
        </Stack>
      );
    })}
  </Stack>
);

const Stages = ({
  axis,
  stages,
  previews,
}: {
  axis: string[];
  stages: Stage[];
  previews?: Record<string, PlanPreview[]>;
}) => {
  const per = Math.max(1, Math.floor(COLUMNS / Math.max(1, axis.length)));
  const [picked, setPicked] = useState(stages[0]?.title ?? '');
  const open = stages.find((stage) => stage.title === picked) ?? stages[0];
  const shown = (open && previews?.[open.title]) || [];

  return (
    <Stack spacing={1.5}>
      <Key />

      {/* Three columns only where three columns fit. On a laptop the diagram,
          the readme and the strip beside each other left the diagram about five
          hundred pixels, which is a track eight pixels wide and the months
          written across each other. Below xl the strip wraps under the two of
          them and gets the full width, which suits a scrolling row better than
          a third of the row did. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          flexWrap: 'wrap',
          gap: 4,
        }}
      >
        {/* Capped: across a full monitor the bars became strips a foot long,
            which says nothing a shorter bar does not.

            Floored as well: the months are the widest thing the row carries, so
            the floor is whatever puts "October onwards" on one line. */}
        <Stack
          sx={{ flex: 1, minWidth: { lg: 720 }, maxWidth: 900 }}
          spacing={0.5}
        >
          {/* The scale. Without it a bar's width is a claim about nothing. */}
          <Box
            sx={{
              ...ROW,
              pb: 0.5,
              borderBottom: 1,
              borderColor: 'custom.neutral.200',
            }}
          >
            <Box />
            {axis.map((label, i) => (
              <Typography
                key={label}
                sx={{
                  display: { xs: 'none', md: 'block' },
                  gridColumn: `${i * per + 2} / span ${per}`,
                  ...LABEL_GAP,
                }}
                variant='overline'
                color='text.secondary'
              >
                {label}
              </Typography>
            ))}
          </Box>

          {stages.map((stage) => {
            const style = STAGE_STYLE[stage.state];
            const isOpen = stage.title === picked;
            return (
              <Box
                key={stage.title}
                onClick={() => setPicked(stage.title)}
                sx={{
                  ...ROW,
                  alignItems: 'center',
                  cursor: 'pointer',
                  borderRadius: `${radius.sm}px`,
                  // Neutral rather than a tint: lavender is retired from this
                  // package's palette, and lime and coral are both spoken for by
                  // the bars.
                  bgcolor: isOpen ? 'custom.neutral.100' : 'transparent',
                  '&:hover': {
                    bgcolor: isOpen
                      ? 'custom.neutral.100'
                      : 'custom.neutral.50',
                  },
                }}
              >
                <Typography sx={{ py: 1, ...LABEL_GAP }} variant='h6'>
                  {stage.title}
                </Typography>

                {/* The bar carries nothing but its position: text inside it
                  made every row as tall as the sentence in it. A stage with no
                  position gets no bar, which says less than a box parked in
                  the last month would. */}
                {stage.from !== undefined && stage.span !== undefined ? (
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      gridColumn: `${stage.from + 1} / span ${stage.span}`,
                      height: 20,
                      bgcolor: style.bg,
                      border: 1,
                      borderStyle: style.dashed ? 'dashed' : 'solid',
                      borderColor: style.border,
                      borderRadius: `${radius.sm}px`,
                    }}
                  />
                ) : (
                  // No timing, but still a state: the key's own swatch at the
                  // start of the track. Its size is the key's, not a bar's, so
                  // it reads as "this is where it has got to" and not as a
                  // month it is booked into.
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      gridColumn: '2 / span 1',
                      width: 24,
                      height: 12,
                      bgcolor: style.bg,
                      border: 1,
                      borderStyle: style.dashed ? 'dashed' : 'solid',
                      borderColor: style.border,
                      borderRadius: `${radius.sm}px`,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Stack>

        {/* What the open row is, and why it sits there. A readme rather than a
          caption, titled with the stage and colour-coded off its state, so the
          block is visibly about the row that is lit. */}
        <Box
          sx={{
            maxWidth: { lg: 320 },
            flexShrink: 0,
            // Down by the height of the month row, so the note's top edge lands
            // on the rule under it rather than level with the months.
            pt: { lg: `${AXIS_HEIGHT}px` },
          }}
        >
          {open && (
            <Note
              title={open.title}
              tone={{
                head: STAGE_STYLE[open.state].bg,
                frame: STAGE_STYLE[open.state].border,
                dashed: STAGE_STYLE[open.state].dashed,
              }}
            >
              {open.definition}

              {/* Footnotes rather than more paragraphs: they are things true of
                  the stage rather than more about what it is. One rule under
                  the definition, however many of them there are. */}
              {open.footnotes?.length ? (
                <Stack
                  sx={{
                    mt: 1.5,
                    pt: 1.5,
                    borderTop: 1,
                    borderColor: 'divider',
                  }}
                  spacing={1}
                >
                  {open.footnotes.map((footnote) => (
                    <Typography
                      key={footnote}
                      variant='caption'
                      color='text.secondary'
                    >
                      {footnote}
                    </Typography>
                  ))}
                </Stack>
              ) : null}
            </Note>
          )}
        </Box>

        {/* The screens the row is about, where there are any. A strip rather
            than one frame: every stage here is a conversation and something
            that comes out of it, and one frame of the something reads as a
            form. Infrastructure has none, because it is internal and there is
            nothing to show. */}
        {shown.length > 0 && (
          <Stack
            sx={{
              flexGrow: 1,
              minWidth: 0,
              // A whole line to itself until there is room for a third column.
              flexBasis: { lg: '100%', xl: 0 },
              // Only worth dropping to the rule when it is beside it.
              pt: { xl: `${AXIS_HEIGHT}px` },
            }}
            spacing={1}
          >
            {/* Said once, above the strip: these are screens that exist,
                not a design of the stage. */}
            <SectionTitle label='PREVIEW' />

            <ScrollRow>
              {/* ScrollRow gives the overflow, not the direction: the strip is
                  the caller's, the same way a flow's is. */}
              <Stack
                sx={{ width: 'max-content', alignItems: 'flex-start' }}
                direction='row'
                spacing={2}
              >
                {shown.map((preview) => (
                  <MiniScreen
                    key={preview.label}
                    label={preview.label}
                    variant={preview.screen ?? 'phone'}
                  >
                    {preview.render()}
                  </MiniScreen>
                ))}
              </Stack>
            </ScrollRow>
          </Stack>
        )}
      </Box>
    </Stack>
  );
};

const PlanPage = ({
  spec,
  previews,
}: {
  spec: PlanSpec;
  /** The screens for each stage, keyed by title. See PlanPreview. */
  previews?: Record<string, PlanPreview[]>;
}) => (
  <Box sx={{ px: 4, py: 5 }}>
    {/* Breadcrumb, as the flows pages have it. */}
    <Stack sx={{ alignItems: 'center', mb: 4 }} direction='row' spacing={1}>
      <Typography variant='overline' color='text.secondary'>
        Plan
      </Typography>
      <Typography variant='overline' color='text.secondary'>
        /
      </Typography>
      <Typography variant='overline'>{spec.title}</Typography>
    </Stack>

    <Stack
      sx={{ alignItems: 'flex-start' }}
      direction='row'
      spacing={6}
      divider={<Divider orientation='vertical' flexItem />}
    >
      {/* The vision is the page's description slot, at h3 rather than body:
          it is the one sentence everything below it is accountable to, so it
          should be readable from across the room. */}
      <Stack sx={{ flex: 1, minWidth: 0 }} spacing={2}>
        <Typography variant='h1'>{spec.title}</Typography>
      </Stack>

      <Stack sx={{ width: 360, flexShrink: 0 }} spacing={4}>
        {spec.links.length > 0 && (
          <Stack spacing={1.5}>
            <SectionTitle label='LINKS' />
            <Stack spacing={1}>
              {spec.links.map((l) => (
                <Stack
                  sx={{ alignItems: 'center', color: 'text.secondary' }}
                  key={l.url}
                  direction='row'
                  spacing={1}
                >
                  <Icon
                    icon={
                      (l.url.startsWith('http')
                        ? 'arrow-up-right-from-square'
                        : 'file-lines') as never
                    }
                    fontSize={14}
                  />
                  <Link
                    href={l.url}
                    target='_blank'
                    rel='noopener'
                    variant='body2'
                    underline='hover'
                  >
                    {l.label}
                  </Link>
                </Stack>
              ))}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>

    <Divider sx={{ my: 5 }} />

    <Stack spacing={5}>
      <Panel label='STRATEGY'>
        <Stack spacing={3}>
          <Typography sx={{ maxWidth: 720 }} variant='body1'>
            {spec.definition}
          </Typography>
          <Stages axis={spec.axis} stages={spec.stages} previews={previews} />
        </Stack>
      </Panel>
    </Stack>
  </Box>
);

export default PlanPage;
